import React from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import BulkImporter from '../BulkImporter/BulkImporter';

export const INVOICE_IMPORT_FIELDS = [
    {
        name: 'description',
        label: 'Line item description',
        description: 'The service, product, or work being billed. Each row creates one invoice.',
        required: true,
        aliases: ['item', 'item description', 'service', 'product'],
    },
    {
        name: 'unitPrice',
        label: 'Unit price',
        description: 'Price per item in USD, such as 25.00 or $25.00.',
        required: true,
        aliases: ['price', 'unit price usd', 'amount', 'rate'],
    },
    {
        name: 'quantity',
        label: 'Quantity',
        description: 'A positive whole number. Defaults to 1.',
        aliases: ['qty'],
    },
    {
        name: 'invoiceNumber',
        label: 'Invoice number',
        description: 'Your optional invoice or reference number.',
        aliases: ['invoice no', 'invoice id', 'reference'],
    },
    {
        name: 'customerName',
        label: 'Customer name',
        description: 'The name shown on the customer-facing invoice.',
        aliases: ['name', 'client name', 'buyer name'],
    },
    {
        name: 'customerEmail',
        label: 'Customer email',
        description: 'Used when you send the imported draft to the customer.',
        aliases: ['email', 'client email', 'buyer email'],
    },
    {
        name: 'dueAt',
        label: 'Due date',
        description: 'Optional date in YYYY-MM-DD format.',
        aliases: ['due', 'due at', 'payment due'],
    },
    {
        name: 'tax',
        label: 'Tax',
        description: 'Optional tax amount in USD. Defaults to 0.',
        aliases: ['tax amount'],
    },
    {
        name: 'shipping',
        label: 'Shipping',
        description: 'Optional shipping amount in USD. Defaults to 0.',
        aliases: ['shipping amount', 'postage'],
    },
    {
        name: 'discount',
        label: 'Discount',
        description: 'Optional discount amount in USD. Defaults to 0.',
        aliases: ['discount amount'],
    },
];

function parseMoney(value, label, required = false) {
    const source = String(value || '').trim();
    if (!source && !required) return 0;

    const normalized = source
        .replace(/^USD\s*/i, '')
        .replace(/[$,\s]/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
        throw new Error(`${label} must be a non-negative USD amount with up to two decimals.`);
    }
    return Math.round(Number(normalized) * 100);
}

function isValidCalendarDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function validateInvoiceImportRecord(record) {
    const errors = [];
    const quantity = record.quantity ? Number(record.quantity) : 1;

    if (!Number.isInteger(quantity) || quantity <= 0) {
        errors.push('Quantity must be a positive whole number.');
    }
    if (record.invoiceNumber.length > 100) {
        errors.push('Invoice number must be 100 characters or fewer.');
    }
    if (record.customerEmail
        && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(record.customerEmail)) {
        errors.push('Customer email is not a valid email address.');
    }
    if (record.dueAt && !isValidCalendarDate(record.dueAt)) {
        errors.push('Due date must be a valid date in YYYY-MM-DD format.');
    }

    try {
        parseMoney(record.unitPrice, 'Unit price', true);
        parseMoney(record.tax, 'Tax');
        parseMoney(record.shipping, 'Shipping');
        parseMoney(record.discount, 'Discount');
    } catch (error) {
        errors.push(error.message);
    }

    return errors;
}

export function transformInvoiceImportRecord(record) {
    return {
        invoiceNumber: record.invoiceNumber || null,
        currency: 'USD',
        lineItems: [{
            description: record.description,
            quantity: record.quantity ? Number(record.quantity) : 1,
            unitPriceCents: parseMoney(record.unitPrice, 'Unit price', true),
        }],
        taxCents: parseMoney(record.tax, 'Tax'),
        shippingCents: parseMoney(record.shipping, 'Shipping'),
        discountCents: parseMoney(record.discount, 'Discount'),
        dueAt: record.dueAt || null,
        customer: {
            name: record.customerName || null,
            email: record.customerEmail || null,
        },
    };
}

export default function InvoiceImportModal({ onClose, onComplete }) {
    const { fetch: authFetch, API_URL } = useAuth();

    const importInvoice = async (payload) => {
        const response = await authFetch(`${API_URL}/v1/seller/invoices`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        const json = await response.json();
        if (!response.ok) {
            throw new Error(json.error || 'Failed to create invoice.');
        }
        return json.data;
    };

    return (
        <BulkImporter
            title="Import invoices from file"
            intro={(
                <>
                    <p>Each data row creates one USD invoice draft with one line item.</p>
                    <p>You can review and send imported drafts from the invoice list.</p>
                </>
            )}
            fields={INVOICE_IMPORT_FIELDS}
            validateRecord={validateInvoiceImportRecord}
            transformRecord={transformInvoiceImportRecord}
            onImport={importInvoice}
            onComplete={onComplete}
            onClose={onClose}
            importLabel="Import invoice drafts"
            recordName="invoice"
            batchSize={4}
        />
    );
}
